import { fetchApiData, postApiData, putApiData, deleteApiData } from '@/helpers/api'
import logger from '@/services/logger'

const buildQuery = (params = {}) => {
  const query = new URLSearchParams()

  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      query.append(key, value)
    }
  })

  const queryString = query.toString()
  return queryString ? `?${queryString}` : ''
}

const normalizeListResponse = (response, listKey, totalKey) => ({
  [listKey]: Array.isArray(response?.[listKey]) ? response[listKey] : [],
  [totalKey]: typeof response?.[totalKey] === 'number' ? response[totalKey] : 0
})

export default {
  namespaced: true,
  actions: {
    async fetchInvoicesList (context, params = {}) {
      try {
        const query = buildQuery({
          limit: params.limit ?? 10,
          page: params.page ?? 1,
          sortBy: params.sortBy || 'created_at',
          order: params.order || 'DESC'
        })

        const response = await fetchApiData(`api/organizations/subscription/invoice${query}`)
        return normalizeListResponse(response, 'invoices', 'totalResults')
      } catch (error) {
        logger.error('Error fetching invoices list from API', {
          error: error.message,
          stack: error.stack
        })

        return {
          invoices: [],
          totalResults: 0,
          error: 'Unable to load invoices. Please try again or contact support.'
        }
      }
    },

    async searchInvoices (context, params = {}) {
      const search = (params.search || params.query || '').trim()
      try {
        if (!search) {
          logger.warn('Search value is required for invoice search')
          return {
            invoices: [],
            totalResults: 0,
            error: 'search value is required'
          }
        }

        const validSortColumns = ['total', 'status', 'due_date', 'created_at', 'updated_at']
        const sortBy = validSortColumns.includes(params.sortBy) ? params.sortBy : 'created_at'
        const order = params.order && params.order.toUpperCase() === 'DESC' ? 'DESC' : 'ASC'

        const query = buildQuery({
          search,
          page: params.page ?? 1,
          limit: params.limit ?? 10,
          sortBy,
          order
        })

        const response = await fetchApiData(`api/organizations/subscription/invoice/search${query}`)
        return normalizeListResponse(response, 'invoices', 'totalResults')
      } catch (error) {
        logger.error('Error searching invoices from API', {
          error: error.message,
          stack: error.stack,
          search
        })

        return {
          invoices: [],
          totalResults: 0,
          error: error.message || 'Unable to search invoices. Please try again or contact support.'
        }
      }
    },

    async createInvoice (context, invoiceData) {
      return postApiData('api/organizations/subscription/invoice', invoiceData)
    },

    async fetchInvoiceSubscriptions () {
      return fetchApiData('api/organization/invoice-subscriptions')
    },

    async fetchInvoiceById (context, invoiceId) {
      return fetchApiData(`api/organizations/subscription/invoice/${encodeURIComponent(invoiceId)}`)
    },

    async updateInvoice (context, { invoiceId, invoiceData }) {
      return putApiData(`api/organizations/subscription/invoice/${encodeURIComponent(invoiceId)}`, invoiceData)
    },

    async deleteInvoice (context, invoiceId) {
      return deleteApiData(`api/organizations/subscription/invoice/${encodeURIComponent(invoiceId)}`)
    },

    async fetchPaymentsList (context, params = {}) {
      try {
        const query = buildQuery({
          limit: params.limit ?? 10,
          page: params.page ?? 1,
          sortBy: params.sortBy || 'created_at',
          order: params.order || 'DESC'
        })

        const response = await fetchApiData(`api/organizations/subscription/invoice/payment${query}`)
        return normalizeListResponse(response, 'payments', 'total_count')
      } catch (error) {
        logger.error('Error fetching payments list from API', {
          error: error.message,
          stack: error.stack
        })

        return {
          payments: [],
          total_count: 0,
          error: 'Unable to load payments. Please try again or contact support.'
        }
      }
    },

    async searchPayments (context, params = {}) {
      const search = (params.search || params.query || '').trim()

      try {
        if (!search) {
          logger.warn('Search value is required for payment search')
          return { payments: [], total_count: 0, error: 'search value is required' }
        }

        const validSortColumns = ['amount', 'status', 'created_at', 'updated_at']
        const sortBy = validSortColumns.includes(params.sortBy) ? params.sortBy : 'created_at'
        const order = params.order && params.order.toUpperCase() === 'ASC' ? 'ASC' : 'DESC'
        const query = buildQuery({
          search,
          limit: params.limit ?? 10,
          page: params.page ?? 1,
          sortBy,
          order
        })

        const response = await fetchApiData(`api/organizations/subscription/invoice/payment/search${query}`)
        return normalizeListResponse(response, 'payments', 'total_count')
      } catch (error) {
        logger.error('Error searching payments from API', { error: error.message, stack: error.stack, search })
        return {
          payments: [],
          total_count: 0,
          error: error.message || 'Unable to search payments. Please try again or contact support.'
        }
      }
    },

    async createPayment (context, paymentData) {
      return postApiData('api/organizations/subscription/invoice/payment', paymentData)
    },

    async fetchPaymentById (context, paymentId) {
      return fetchApiData(`api/organizations/subscription/invoice/payment/${encodeURIComponent(paymentId)}`)
    },

    async fetchPaymentsByInvoiceId (context, invoiceId) {
      return fetchApiData(`api/organizations/subscription/invoice/payment/invoice/${encodeURIComponent(invoiceId)}`)
    },

    async updatePayment (context, { paymentId, paymentData }) {
      return putApiData(`api/organizations/subscription/invoice/payment/${encodeURIComponent(paymentId)}`, paymentData)
    }
  }
}
